import * as zod from 'zod';

import type { DeviceInfo } from '@sb/messenger/auto_generated/messenger/skills_bot_service';
import type { PreloadedModelInfo } from '@sb/messenger/auto_generated/messenger/vision_bot_service';

/**
 * Base configuration for completion detection methods.
 * - For learned completion/failure: threshold is the model prediction confidence (0-1)
 * - For movement completion: threshold is the position delta in mm (e.g., 5 = 5mm)
 */
const CompletionConfig = zod.object({
  enabled: zod.boolean().default(false),
  threshold: zod.number(),
  consecutiveCount: zod.number().int(),
});

/**
 * Movement completion configuration.
 * threshold: position delta in mm. rotationThreshold: max rotation magnitude in degrees; if exceeded, movement completion fails.
 * gripperThreshold: if gripper delta across horizon exceeds this, movement completion fails. Default 0.1.
 */
const MovementCompletionConfig = CompletionConfig.extend({
  rotationThreshold: zod.number().default(5), // degrees
  gripperThreshold: zod.number().default(0.1),
});

/**
 * Consolidated completion prediction configuration for start_inference command
 */
export const CompletionPredictionConfig = zod.object({
  learnedCompletion: CompletionConfig.default({
    enabled: false,
    threshold: 0.75,
    consecutiveCount: 2,
  }),
  learnedFailure: CompletionConfig.default({
    enabled: false,
    threshold: 0.75,
    consecutiveCount: 2,
  }),
  movementCompletion: MovementCompletionConfig.default({
    enabled: false,
    threshold: 5, // mm default
    consecutiveCount: 3,
    rotationThreshold: 5, // degrees default
    gripperThreshold: 0.1,
  }),
});

export type CompletionPredictionConfig = zod.infer<
  typeof CompletionPredictionConfig
>;

// Skill subcommand types
export const GPUTaskRunnerSkillSubCommand = zod.union([
  zod.object({
    command: zod.literal('start_inference'),
    timeout: zod.number(),
    modelName: zod.string(),
    completionPrediction: CompletionPredictionConfig.optional(),
    caption: zod.string().nullable().optional(),
    frequency: zod.number().optional(),
    stepsPerInference: zod.number().optional(),
    maxLinearSpeed: zod.number().nullable().optional(),
    actionExecutionMode: zod.string().optional(),
    actionInterpolationMethod: zod.string().optional(),
    outputSmoothingAlpha: zod.number().optional(),
    enablePredictionLogging: zod.boolean().optional(),
    lockedAxesPerRobot: zod
      .record(zod.string(), zod.array(zod.number().int().min(0).max(6)))
      .optional(),
    axisLockingFramePerRobot: zod
      .record(zod.string(), zod.enum(['world', 'wrist']))
      .optional(),
  }),
  zod.object({
    command: zod.literal('stop_inference'),
  }),
  zod.object({
    command: zod.literal('configure_inference'),
    modelName: zod.string(),
    robotIdsMap: zod.record(zod.string(), zod.string()), // {[robot key]: [robot ID]}, e.g. {'robot_0': 'bot_0sapi_rvVAMObHwVGCkTkpVSY6'}
    equipmentKeyToDeviceInfo: zod.record(
      zod.string(),
      zod.custom<DeviceInfo>(),
    ),
    useGripperMask: zod.boolean().optional(),
    robotConfig: zod.string(),
    obsHorizon: zod.number().optional(),
    obsRes: zod.array(zod.number()).optional(), // [width, height]
    robotControllerFrequency: zod.number().optional(),
  }),
  zod.object({
    command: zod.literal('teardown_inference'),
  }),
  zod.object({
    command: zod.literal('delete_model'),
    modelName: zod.string(),
  }),
]);

// Vision subcommand types
const GPUTaskRunnerRunInferenceSubCommand = zod.object({
  command: zod.literal('run_inference'),
  image: zod.string(), // base64 encoded image
  model_path: zod.string(),
  model_type: zod.string(), // Valid values: 'YoloV11', 'YoloV8-OBB', 'TextPrompt'
  thresholding_params: zod.record(zod.string(), zod.string()).optional(),
  include_masks: zod.boolean().optional(), // request SAM2 masks in Blob2D response
});

const GPUTaskRunnerPreloadRoboflowWorkflowSubCommand = zod.object({
  command: zod.literal('preload_roboflow_workflow'),
  workspaceName: zod.string(),
  workflowId: zod.string(),
});

// Runs a workflow and reduces one named output to detections. `outputName` is
// required rather than inferred: a workflow can publish several detection-bearing
// outputs, so the choice is made at selection time from the workflow's schema.
const GPUTaskRunnerRunRoboflowWorkflowDetectionSubCommand = zod.object({
  command: zod.literal('run_roboflow_workflow_detection'),
  image: zod.string(), // base64 encoded image
  workspaceName: zod.string(),
  workflowId: zod.string(),
  outputName: zod.string(),
  // Applied by vision-bot after the run, on the chosen output's predictions.
  thresholding_params: zod.record(zod.string(), zod.string()).optional(),
  // Bound to the workflow's own inputs to neutralise in-graph filtering, so the
  // thresholds above are the only ones that apply. Only effective for workflows
  // that declare those inputs; the rest filter at their block defaults.
  workflow_inputs: zod.record(zod.string(), zod.string()).optional(),
  // Set by editor previews. See `preview` in vision_bot_service.proto.
  preview: zod.boolean().optional(),
});

export const GPUTaskRunnerVisionSubCommand = zod.discriminatedUnion('command', [
  GPUTaskRunnerRunInferenceSubCommand,
  GPUTaskRunnerPreloadRoboflowWorkflowSubCommand,
  GPUTaskRunnerRunRoboflowWorkflowDetectionSubCommand,
]);

/**
 * What the inference container reported about one model the preload warmed.
 *
 * The proto message as decoded, rather than a restatement of it: the fields and
 * their meanings are documented on `PreloadedModelInfo` itself.
 */
export type GPUTaskRunnerPreloadedModel = PreloadedModelInfo;

/** Result of a Roboflow preload vision subcommand (returned to the browser). */
export type GPUTaskRunnerRoboflowPreloadResult =
  | {
      ok: true;
      loadTimeMs?: number;
      /**
       * One entry per model the graph runs, so a chained workflow reports every
       * one. Empty when the container's cache was unreadable — the preload's own
       * work is warming the weights, and this rides along.
       */
      models: GPUTaskRunnerPreloadedModel[];
      /**
       * Whether the workflow holds a Dataset Upload or Vision Events block.
       *
       * Three-valued: `undefined` means the robot could not say, because its
       * vision-bot predates the field or could not read the cached definition;
       * `false` means the graph was inspected and holds neither. Collapsed together, the UI would tell an operator their
       * workflow sends nothing when the robot simply cannot say.
       */
      declaresRoboflowUpload?: boolean;
    }
  | { ok: false; error: string };

export const GPUTaskRunnerCommand = zod.object({
  kind: zod.literal('GPUTaskRunnerCommand'),
  equipmentId: zod.optional(zod.string()),
  commandType: zod.enum(['skill', 'vision']),
  subCommand: zod.union([
    GPUTaskRunnerSkillSubCommand,
    GPUTaskRunnerVisionSubCommand,
  ]),
});

export type GPUTaskRunnerCommand = zod.infer<typeof GPUTaskRunnerCommand>;
export type GPUTaskRunnerSkillSubCommand = zod.infer<
  typeof GPUTaskRunnerSkillSubCommand
>;
export type GPUTaskRunnerVisionSubCommand = zod.infer<
  typeof GPUTaskRunnerVisionSubCommand
>;
